import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { api } from '@/lib/api';

export type Theme =
  | 'ledger-navy' | 'vault-green' | 'platinum-statement' | 'midnight-spreadsheet'
  | 'emerald-equity' | 'graphite-reserve' | 'rose-gold' | 'indigo-ledgerlines'
  | 'silver-interest' | 'cloudy-cashflow' | 'harbor-balance'
  | 'emerald-sophistication' | 'platinum-prestige' | 'sapphire-dreams'
  | 'obsidian-gold' | 'arctic-luxury';

export type ThemeMode = 'dark' | 'light' | 'auto';

export const THEMES: { id: Theme; label: string; accent: string }[] = [
  // Finance
  { id: 'ledger-navy', label: 'Ledger Navy', accent: '#2F5D8C' },
  { id: 'vault-green', label: 'Vault Green', accent: '#5FB88A' },
  { id: 'platinum-statement', label: 'Platinum Statement', accent: '#9CA3AF' },
  { id: 'midnight-spreadsheet', label: 'Midnight Spreadsheet', accent: '#3B82F6' },
  { id: 'emerald-equity', label: 'Emerald Equity', accent: '#34D399' },
  { id: 'graphite-reserve', label: 'Graphite Reserve', accent: '#6B7280' },
  { id: 'rose-gold', label: 'Rose Gold Forecast', accent: '#D08C9B' },
  { id: 'indigo-ledgerlines', label: 'Indigo Ledgerlines', accent: '#7C83FD' },
  { id: 'silver-interest', label: 'Silver Interest', accent: '#94A3B8' },
  { id: 'cloudy-cashflow', label: 'Cloudy Cashflow', accent: '#A8B3C5' },
  { id: 'harbor-balance', label: 'Harbor Balance', accent: '#0FA3B1' },
  // Luxury
  { id: 'emerald-sophistication', label: 'Emerald Sophistication', accent: '#7ba05b' },
  { id: 'platinum-prestige', label: 'Platinum Prestige', accent: '#bdc3c7' },
  { id: 'sapphire-dreams', label: 'Sapphire Dreams', accent: '#4a90a4' },
  { id: 'obsidian-gold', label: 'Obsidian Gold', accent: '#bea98e' },
  { id: 'arctic-luxury', label: 'Arctic Luxury', accent: '#b0c4de' },
];

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  setTheme: (t: Theme) => void;
  setThemeMode: (m: ThemeMode) => void;
}

const ThemeCtx = createContext<ThemeContextType>({
  theme: 'platinum-statement',
  themeMode: 'dark',
  setTheme: () => {},
  setThemeMode: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem('fundingos-theme') as Theme) || 'platinum-statement'
  );
  const [themeMode, setThemeModeState] = useState<ThemeMode>(
    () => (localStorage.getItem('fundingos-theme-mode') as ThemeMode) || 'dark'
  );

  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistToBackend = useCallback((patch: Record<string, string>) => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      api.updateBrokerProfile(patch).catch(() => {});
    }, 500);
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem('fundingos-theme', t);
    persistToBackend({ theme: t });
  }, [persistToBackend]);

  const setThemeMode = useCallback((m: ThemeMode) => {
    setThemeModeState(m);
    localStorage.setItem('fundingos-theme-mode', m);
    persistToBackend({ theme_mode: m });
  }, [persistToBackend]);

  // Hydrate from backend on first load (cross-device sync)
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    if (localStorage.getItem('fundingos-theme')) return;
    // Public surfaces (client portal, /start front door) have no broker session — skip
    // the broker-profile hydrate so /brokers/me doesn't 401 (and bounce /start to /login).
    if (window.location.pathname.startsWith('/portal') || window.location.pathname.startsWith('/start')) return;
    api.getBrokerProfile().then((profile) => {
      const t = (profile as Record<string, unknown>).theme as Theme | undefined;
      const m = (profile as Record<string, unknown>).theme_mode as ThemeMode | undefined;
      if (t && THEMES.some((th) => th.id === t)) {
        setThemeState(t);
        localStorage.setItem('fundingos-theme', t);
      }
      if (m && ['dark', 'light', 'auto'].includes(m)) {
        setThemeModeState(m);
        localStorage.setItem('fundingos-theme-mode', m);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);

    const effectiveMode =
      themeMode === 'auto'
        ? window.matchMedia('(prefers-color-scheme: light)').matches
          ? 'light'
          : 'dark'
        : themeMode;

    root.setAttribute('data-theme-mode', effectiveMode);
  }, [theme, themeMode]);

  return (
    <ThemeCtx.Provider value={{ theme, themeMode, setTheme, setThemeMode }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useThemeContext() {
  return useContext(ThemeCtx);
}
